import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { toast } from "react-toastify";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";

import {
  API_generate_swipe_deck_with_ai,
  API_get_learning_swipe_deck_detail,
  API_get_learning_file,
  API_post_learning_swipe_deck,
  API_post_set_swipe_deck_public_share,
} from "../../../constants";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import { streamSwipeDeckGenerationAPI } from "../requests/utilities/streamingRequests";
import {
  makeDefaultDeck,
  makeDefaultCard,
  buildDeckSavePayload,
  normalizeBackendDeck,
  uploadLearningFile,
  generateSwipeCardImage,
} from "./swipeDeckHelpers";
import AiSwipeDeckGeneratorModal from "./AiSwipeDeckGeneratorModal";
import ReuseSignalBanner from "./ReuseSignalBanner";
import DiscardChangesConfirm from "./DiscardChangesConfirm";
import DirectAssignabilityToggle, {
  setDirectAssignability,
} from "./DirectAssignabilityToggle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import IosShareIcon from "@mui/icons-material/IosShare";
import SwipeDeckPollResultsModal from "./SwipeDeckPollResultsModal";
import QrCodePopover from "./QrCodePopover";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import { SwipeCardTypePickerModal } from "./SwipeCardTypePickerModal";
import { SwipeVideoSplitModal } from "./SwipeVideoSplitModal";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import { SwipeCardImageTextEditor } from "./SwipeCardImageTextEditor";
import { SwipeCardVideoEditor } from "./SwipeCardVideoEditor";
import { SwipeCardPollEditor } from "./SwipeCardPollEditor";
import { SwipeCardTriviaEditor } from "./SwipeCardTriviaEditor";
import { ScaledCardPreview } from "./ScaledCardPreview";

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Constants

const CARD_TYPE_META = {
  image_text: { label: "Image + Text", Icon: ImageIcon },
  video: { label: "Video", Icon: OndemandVideoIcon },
  poll: { label: "Poll", Icon: PollOutlinedIcon },
  quiz: { label: "Trivia", Icon: QuestionAnswerOutlinedIcon },
};

// Light-tint backgrounds + saturated foregrounds. Each pair mirrors the
// solid-color theme used for the same card type in SwipeCardTypePickerModal,
// so admins see a consistent color identity across the picker, the card
// list, and the storyboard tile badges.
const TYPE_BADGE_BG = {
  image_text: "#ede9fe", // violet-100
  video: "#dbeafe", // blue-100
  poll: "#fef3c7", // amber-100
  quiz: "#dcfce7", // green-100
};

const TYPE_BADGE_FG = {
  image_text: "#6d28d9", // violet-700
  video: "#1e40af", // blue-800
  poll: "#b45309", // amber-700
  quiz: "#15803d", // green-700
};

const modalStyle = {
  // Center without `transform` — @hello-pangea/dnd's drag clone uses
  // `position: fixed`, which becomes relative to the nearest transformed
  // ancestor instead of the viewport. With a transformed modal wrapping
  // the card list, dragging inside a scrolled list visibly offsets the
  // ghost. `inset: 0 + margin: auto` centers identically using
  // max-width/max-height as the size cap.
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "100%",
  height: "100%",
  maxWidth: "1600px",
  maxHeight: "96vh",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Main modal

/**
 * Storyboard-style swipe deck editor.
 *
 * Three-pane layout:
 *   - Left: vertical card list, drag-to-reorder, +Add card
 *   - Center: storyboard grid of card preview tiles
 *   - Right: contextual editor for the selected card
 *
 * Top bar carries the deck name plus the primary actions (Preview,
 * Save). Save posts the full deck via post_learning_swipe_deck which
 * upserts cards by UUID.
 */
function SwipeDeckInputsModal(props) {
  const {
    open,
    onClose,
    modalDataDeck, // { uuid } when editing, null/undefined when creating
    homeCompany,
    onSaved,
    onPublicShareChange, // () => parent refreshes the decks list's share state
    onPreview, // (deck) => parent opens player in preview mode
    sectionsData, // for the AI generator's content picker
    modulesData,
    // "training" (default) or "engagement" (the Engagement tab). Same editor;
    // the Engagement variant hides assignability and section usage, saves with
    // purpose="engagement", and shows public poll results only.
    variant = "training",
  } = props;
  const isEngagement = variant === "engagement";

  const [deck, setDeck] = React.useState(makeDefaultDeck());
  const [selectedCardIdx, setSelectedCardIdx] = React.useState(null);
  const [showCardTypePicker, setShowCardTypePicker] = React.useState(false);
  // Left card-list panel is collapsible to give the storyboard more room
  // (the fluid grid then grows the tiles into the reclaimed width).
  const [showCardList, setShowCardList] = React.useState(true);
  const [showVideoSplitModal, setShowVideoSplitModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [uploadingCover, setUploadingCover] = React.useState(false);
  // Reuse signal: how many sections embed this deck (null = unknown/create).
  const [sectionUsageCount, setSectionUsageCount] = React.useState(null);
  const [directlyAssignable, setDirectlyAssignable] = React.useState(false);
  // Public share link state ({ is_enabled, share_token, open_count }) and the
  // share popover anchor.
  const [publicShare, setPublicShare] = React.useState(null);
  const [shareAnchorEl, setShareAnchorEl] = React.useState(null);
  const [shareSaving, setShareSaving] = React.useState(false);
  // Per-poll-card vote aggregates (keyed by card uuid) for the read-only
  // Results block in the poll editor + the deck-level Poll results summary.
  // Loaded with the deck; read-only.
  const [pollResults, setPollResults] = React.useState({});
  const [pollResultsOpen, setPollResultsOpen] = React.useState(false);
  // "Close without saving?" guard (the X sits next to Create / Save).
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  // Loaded value, to detect whether the assignability setting changed on Save.
  const originalAssignableRef = React.useRef(false);
  // Snapshot of the deck content as last loaded/saved — compared against the
  // live deck to flag unsaved changes for the Share popover. null until seeded.
  const deckBaselineRef = React.useRef(null);
  // Holds a savedDeck from an in-place (Share) save whose parent callback was
  // deferred — replayed on close so a rename/edit reaches the embedding section
  // editor even if the user closes with X instead of a closing Save.
  const pendingSavedDeckRef = React.useRef(null);
  // Set when editing an existing deck whose detail failed to load. Blocks Save
  // so a failed/empty load can't overwrite the deck (and its cards).
  const [loadError, setLoadError] = React.useState(false);

  // AI generation state — same shape as QuizEditorModal's aiGenStatus.
  // null when idle; { current, total } while streaming.
  const [aiGenStatus, setAiGenStatus] = React.useState(null);
  const [showAiModal, setShowAiModal] = React.useState(false);
  const [addCardMenuAnchor, setAddCardMenuAnchor] = React.useState(null);

  // Split-video card resolution: first-seen timestamps per pending segment uuid
  // (for the give-up deadline), a set of uuids we've stopped polling (gave up on
  // without clearing — non-destructive backstop), and a once-per-batch guard for
  // the failure toast.
  const pendingVideoPollRef = React.useRef({});
  const gaveUpVideoPollRef = React.useRef(new Set());
  const splitFailNotifiedRef = React.useRef(false);

  // AbortController for the in-flight AI card stream. Aborted on close /
  // save / unmount so late SSE events can't mutate `deck` after the
  // modal goes away — without this, events that arrive post-close keep
  // running setState against a hidden modal until the next open() wipes
  // local state, which both wastes work and produces React warnings.
  const aiAbortRef = React.useRef(null);

  // = = = AI card-image generation queue = = =
  // The SINGLE owner of card-image generation: the post-stream
  // auto-illustration AND the per-card "Generate image" banner button
  // both flow through here, keyed by _clientId — so a generation
  // started for card 3 lands on card 3 no matter what the author
  // selects, edits, or reorders while the 15-30s render runs.
  // Concurrency-capped so one deck doesn't fan out 10 simultaneous
  // backend workers; cards illustrate progressively as results land.
  // Session counter (not AbortController): the underlying requests
  // complete server-side regardless, so cancellation here just means
  // "stop attaching results + drop the queue" on close/save/new-run.
  const AI_IMAGE_CONCURRENCY = 3;
  // _clientIds of cards whose image is queued or in flight — drives the
  // per-tile shimmer, the banner's generating state, and (with
  // aiImageTotal) the header chip. Single source of truth; "done" is
  // derived (total - size) rather than tracked separately.
  const [generatingImageIds, setGeneratingImageIds] = React.useState(
    () => new Set(),
  );
  const [aiImageTotal, setAiImageTotal] = React.useState(0);
  const aiImageQueueRef = React.useRef([]);
  const aiImageActiveRef = React.useRef(0);
  const aiImageSessionRef = React.useRef(0);
  // _clientIds whose in-flight AI image the author has overridden by
  // manually changing that card's media (upload, replace, or remove)
  // before the render returned. The late result is dropped — a manual
  // decision made after the job was queued always wins over the job.
  const aiImageInvalidatedRef = React.useRef(new Set());
  // The deck name seeds the image prompt's workplace context; read via
  // ref so queued jobs see the latest name without re-rendering closures.
  const deckNameRef = React.useRef("");
  deckNameRef.current = deck.name || "";
  // The current AI run's source documents — passed to the card-image
  // endpoint so a strong real-photo match wins over generation. Set in
  // handleAiGenerate; manual banner-button regenerates leave it as-is
  // (a deck loaded for editing has no run sources, which is correct —
  // those cards already chose their media).
  const aiSourceFileUuidsRef = React.useRef([]);
  // Whether the active run may AI-generate when no real photo matches.
  // False = "photos only" mode (reuse from references, otherwise leave the
  // card unillustrated). Defaults true so manual banner regenerates — which
  // run outside a generation run — always generate, the right behavior.
  const aiAllowGenerationRef = React.useRef(true);
  // Content hashes (sha1) of reused document photos already placed in this
  // run — passed to each card-image request so the same picture (or a
  // byte-identical copy from another page) isn't reused on a second card.
  // Best-effort across the ≤3 concurrent jobs: a job that starts after
  // another finishes sees its hash; truly simultaneous starts can still
  // collide, but that's bounded by AI_IMAGE_CONCURRENCY.
  const usedImageSha1sRef = React.useRef(new Set());

  const cancelInflightAiImages = () => {
    aiImageSessionRef.current += 1;
    aiImageQueueRef.current = [];
    aiImageInvalidatedRef.current = new Set();
    // Release the canceled jobs' concurrency slots NOW — their HTTP
    // requests can't be aborted server-side and may take up to the
    // endpoint timeout to settle, and a new run must not queue behind
    // slots held by results nobody will use. The settle-side decrement
    // in pumpAiImageQueue is session-guarded so these zombies don't
    // double-release (and drive the count negative) when they finish.
    // Cost: a brief window where old-session renders still occupy
    // backend workers while the new session starts its own — bounded at
    // 2× AI_IMAGE_CONCURRENCY.
    aiImageActiveRef.current = 0;
    setGeneratingImageIds(new Set());
    setAiImageTotal(0);
  };

  const runAiImageJob = async (job) => {
    try {
      const { uuid, url, layoutVariant, sha1 } = await generateSwipeCardImage(
        homeCompany,
        job.suggestion,
        deckNameRef.current,
        job.fileUuids || [],
        Array.from(usedImageSha1sRef.current),
        job.allowGeneration !== false,
        job.headline || "",
      );
      if (job.session !== aiImageSessionRef.current) return;
      // "Photos only" with no matching real photo — the backend returns no
      // file. Leave the card unillustrated (its suggestion banner stays for
      // manual fill); not an error.
      if (!uuid) return;
      // Author overrode this card's media while we were rendering —
      // their choice stands; the generated file becomes an orphan
      // (same accepted trade-off as closing mid-generation).
      if (aiImageInvalidatedRef.current.has(job.clientId)) return;
      // Record the reused photo's hash so the remaining cards exclude it
      // (no effect for generated images, which return sha1=null).
      if (sha1) usedImageSha1sRef.current.add(sha1);
      setDeck((prev) => ({
        ...prev,
        cards: prev.cards.map((c) =>
          // Belt to the invalidation suspenders: never overwrite media
          // that exists by the time we land, and never force the hero
          // layout onto a card the author has since set to text_only —
          // a text_only card doesn't want an image, however the state
          // got there.
          c._clientId === job.clientId &&
          !c.media_file_uuid &&
          !c.media_url &&
          c.layout_variant !== "text_only"
            ? {
                ...c,
                media_file_uuid: uuid,
                media_url: url,
                // Honor the backend's aspect-based pick (landscape document
                // photo → "split", else "full_bleed") instead of always
                // forcing the hero layout.
                layout_variant: layoutVariant || "full_bleed",
              }
            : c,
        ),
      }));
    } catch (err) {
      // Per-image failure is non-fatal — the card keeps its suggestion
      // banner and the author can retry from there. Batch jobs stay
      // silent (a flaky batch would stack 6 toasts; the banner is the
      // recovery surface); a manual button click gets a toast because
      // the author is watching that specific card.
      console.error("AI card image generation failed", err);
      if (job.manual && job.session === aiImageSessionRef.current) {
        toast.error("Image generation failed. Please try again.", {
          autoClose: 3000,
        });
      }
    } finally {
      if (job.session === aiImageSessionRef.current) {
        // Invalidation marker is per-flight; clear it so a future
        // re-generate on this card starts clean.
        aiImageInvalidatedRef.current.delete(job.clientId);
        setGeneratingImageIds((prev) => {
          const next = new Set(prev);
          next.delete(job.clientId);
          if (next.size === 0) setAiImageTotal(0);
          return next;
        });
      }
    }
  };

  const pumpAiImageQueue = () => {
    while (
      aiImageActiveRef.current < AI_IMAGE_CONCURRENCY &&
      aiImageQueueRef.current.length > 0
    ) {
      const job = aiImageQueueRef.current.shift();
      if (job.session !== aiImageSessionRef.current) continue;
      aiImageActiveRef.current += 1;
      runAiImageJob(job).finally(() => {
        // Only release a slot for CURRENT-session jobs. A canceled
        // session's jobs had their slots bulk-released by
        // cancelInflightAiImages (active reset to 0); decrementing
        // again here would drive the count negative and quietly raise
        // the effective concurrency cap for the rest of the session.
        if (job.session === aiImageSessionRef.current) {
          aiImageActiveRef.current -= 1;
        }
        // Always re-pump: a zombie settling is still a fine moment to
        // check whether queued current-session jobs can start.
        pumpAiImageQueue();
      });
    }
  };

  const enqueueAiCardImage = (clientId, suggestion, { manual = false, headline = "" } = {}) => {
    // A fresh enqueue supersedes any stale invalidation from an earlier
    // overridden flight on this card.
    aiImageInvalidatedRef.current.delete(clientId);
    aiImageQueueRef.current.push({
      clientId,
      suggestion,
      manual,
      // Card headline (dish/wine name) → backend vision tiebreaker.
      headline,
      // Snapshot the run's source docs onto the job so a real-photo
      // match is tried before generation. Empty for manual banner
      // regenerates on a loaded deck (no run sources) — those just
      // generate, which is the right behavior.
      fileUuids: aiSourceFileUuidsRef.current || [],
      // A manual banner-button retry is an explicit "make me an image"
      // intent, so always allow generation — otherwise a prior "photos only"
      // run leaves the ref false and the manual click silently no-ops on any
      // card without a document-photo match. Batch jobs honor the run's
      // policy (the ref is set per run in handleAiGenerate).
      allowGeneration: manual ? true : aiAllowGenerationRef.current,
      session: aiImageSessionRef.current,
    });
    setGeneratingImageIds((prev) => new Set(prev).add(clientId));
    setAiImageTotal((prev) => prev + 1);
    pumpAiImageQueue();
  };

  // Banner-button entry point: resolve the card NOW (validity check)
  // but attach by _clientId so the result follows the card, not the
  // selection. Already-in-flight cards are a no-op (the banner is
  // disabled while generating, this is belt-and-suspenders).
  const handleGenerateCardImage = (clientId) => {
    if (!clientId || generatingImageIds.has(clientId)) return;
    const card = (deck.cards || []).find((c) => c._clientId === clientId);
    const suggestion = card?.metadata?.media_suggestion;
    if (!card || card.card_type !== "image_text" || !suggestion) return;
    enqueueAiCardImage(clientId, suggestion, {
      manual: true,
      headline: card?.content_json?.headline || "",
    });
  };

  const handleCoverPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploadingCover(true);
    try {
      const { uuid, url } = await uploadLearningFile(homeCompany, file);
      setDeck((prev) => ({
        ...prev,
        cover_file_uuid: uuid,
        cover_url: url,
      }));
    } catch (err) {
      toast.error(err?.message || "Cover upload failed. Please try again.");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleCoverClear = () => {
    setDeck((prev) => ({ ...prev, cover_file_uuid: null, cover_url: null }));
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Load on open

  React.useEffect(() => {
    if (!open) return;
    setSelectedCardIdx(null);
    // Fresh editing session: clear the previous run's source documents
    // so a manual banner regenerate on THIS deck doesn't real-photo-
    // match against the last session's references. handleAiGenerate
    // repopulates it per run.
    aiSourceFileUuidsRef.current = [];
    // Reset to a clean default before kicking off the load. Without
    // this, if loadDeck rejects (network blip on reopen), the previous
    // session's edits stay visible — the admin would think they're
    // editing the persisted deck while really staring at leftover
    // local state. A blank shell is honest about the load failure.
    setDeck(makeDefaultDeck());
    // Baseline for a NEW deck is the blank deck, so the close guard sees any
    // name / description / cover / card as work. loadDeck replaces it for an
    // existing deck.
    deckBaselineRef.current = deckContentSnapshot(makeDefaultDeck());
    pendingSavedDeckRef.current = null;
    setSectionUsageCount(null);
    setLoadError(false);
    setDirectlyAssignable(false);
    originalAssignableRef.current = false;
    setPublicShare(null);
    setShareAnchorEl(null);
    setPollResults({});
    setPollResultsOpen(false);
    if (modalDataDeck?.uuid) {
      loadDeck(modalDataDeck.uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modalDataDeck?.uuid]);

  // Prefetch the lazy-loaded player chunk while the admin is authoring.
  // Without this, the first Preview click pays for a network fetch +
  // chunk eval + the Suspense fallback flash, which feels glitchy
  // against the player's mount animations. Same import path as the
  // React.lazy declaration in CreateTrainingContentDisplay.js — the
  // bundler dedupes to one chunk.
  React.useEffect(() => {
    if (!open) return;
    import(
      "../take_training_employee/swipe_deck_player/SwipeDeckPlayer"
    );
  }, [open]);

  // Canonical content snapshot for unsaved-changes detection: the exact payload
  // that would be persisted (sans company_id), so any content/card/setting edit
  // registers as dirty.
  const deckContentSnapshot = (d) => {
    try {
      return JSON.stringify(buildDeckSavePayload(d));
    } catch (e) {
      return "";
    }
  };

  const loadDeck = async (deckUuid) => {
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_learning_swipe_deck_detail, {
        company_id: homeCompany,
        learning_swipe_deck_uuid: deckUuid,
        include_section_usage: true,
      });
      const beDeck =
        res?.status === 200
          ? res.data?.redux?.payload?.learning_swipe_deck
          : null;
      if (!beDeck) {
        // Deleted deck, permission error, or transient failure. The editor is
        // still on the blank default (uuid null), so leaving it editable would
        // let a Save persist that blank as a brand-new junk deck. Flag the
        // failure to block Save.
        throw new Error("Deck detail not returned");
      }
      const normalized = normalizeBackendDeck(beDeck);
      setDeck(normalized);
      // Baseline for unsaved-changes detection — same normalized deck that seeds
      // state, so we start "clean".
      deckBaselineRef.current = deckContentSnapshot(normalized);
      setDirectlyAssignable(!!beDeck.is_directly_assignable);
      originalAssignableRef.current = !!beDeck.is_directly_assignable;
      setPublicShare(beDeck.public_share || null);
      setPollResults(beDeck.poll_results || {});
      if (beDeck.section_usage_count != null) {
        setSectionUsageCount(beDeck.section_usage_count);
      }
    } catch (err) {
      console.error("Failed to load deck", err);
      toast.error("Couldn't load deck.");
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Card list mutations

  const handleAddCard = (cardType) => {
    const newCard = makeDefaultCard(cardType);
    setDeck((prev) => {
      const nextCards = [...prev.cards, { ...newCard, order_index: prev.cards.length }];
      // Auto-select the new card so the right pane focuses immediately
      Promise.resolve().then(() => setSelectedCardIdx(nextCards.length - 1));
      return { ...prev, cards: nextCards };
    });
    setShowCardTypePicker(false);
  };

  // Append one video card per clip produced by the video splitter. Each
  // segment is its own LearningFile (still PENDING normalization); the cards
  // carry the media_file_uuid and poll to READY like any single upload. Mirrors
  // handleAddCard's append + auto-select, but for N cards at once.
  const handleCreateCardsFromSplit = (createdSegments, layoutVariant) => {
    if (!createdSegments?.length) return;
    // Fresh split → allow one "a clip failed" toast for this batch.
    splitFailNotifiedRef.current = false;
    setDeck((prev) => {
      const base = prev.cards.length;
      const newCards = createdSegments.map((seg, i) => ({
        ...makeDefaultCard("video"),
        // Auto-picked from the source aspect by the splitter: landscape →
        // "split" (no cropping/letterboxing), portrait/square → "full_bleed".
        layout_variant: layoutVariant || "full_bleed",
        media_file_uuid: seg.learning_file_uuid,
        // media_url resolves once the segment finishes optimizing (see the
        // pending-video poll below) or when the card is opened in the editor.
        media_url: null,
        order_index: base + i,
        content_json: {
          title: seg.title || "",
          caption: seg.caption || "",
          captions_burned_in: false,
        },
      }));
      Promise.resolve().then(() => setSelectedCardIdx(base));
      return { ...prev, cards: [...prev.cards, ...newCards] };
    });
  };

  // Resolve media URLs for video cards still being optimized (freshly split
  // segments arrive PENDING). The per-card editor only polls the SELECTED card,
  // so without this the un-selected split cards' tiles would stay blank until
  // clicked. Keyed on the set of pending uuids so it doesn't restart on every
  // keystroke; stops once none remain.
  const pendingVideoKey = (deck.cards || [])
    .filter(
      (c) =>
        c.card_type === "video" &&
        c.media_file_uuid &&
        !c.media_url &&
        !gaveUpVideoPollRef.current.has(c.media_file_uuid),
    )
    .map((c) => c.media_file_uuid)
    .join(",");
  // Drop a dead clip (failed normalization, or polled so long the worker
  // almost certainly never ran) from its card. The card reverts to an empty
  // video card — this both stops the poll (no media_file_uuid) and unblocks
  // save (the readiness gate only rejects bound-but-unready clips).
  const clearDeadVideoCard = (fileUuid) => {
    setDeck((prev) => ({
      ...prev,
      cards: prev.cards.map((c) =>
        c.card_type === "video" && c.media_file_uuid === fileUuid
          ? { ...c, media_file_uuid: null, media_url: null }
          : c,
      ),
    }));
    if (!splitFailNotifiedRef.current) {
      splitFailNotifiedRef.current = true;
      toast.error("A video clip couldn't be processed and was cleared from its card.");
    }
  };
  React.useEffect(() => {
    if (!pendingVideoKey) return undefined;
    const uuids = pendingVideoKey.split(",");
    // The split task always marks each segment ready/failed (even on a download
    // error or soft-timeout), so the only way a card stays PENDING forever is a
    // genuine broker/worker outage. This deadline is just that backstop — set it
    // past the task's ~55-min hard limit plus queue wait, and make hitting it
    // NON-DESTRUCTIVE (we stop polling but never clear the card; only an
    // explicit "failed" clears, since clearing a clip that later finishes would
    // lose the author's work). Record first-seen per uuid.
    const GIVE_UP_MS = 60 * 60 * 1000;
    const seenAt = pendingVideoPollRef.current;
    uuids.forEach((u) => {
      if (!seenAt[u]) seenAt[u] = Date.now();
    });
    let cancelled = false;
    const tick = async () => {
      // Parallel, not sequential: a 20-clip split shouldn't take 20×latency per
      // tick (which could overrun the interval).
      const results = await Promise.allSettled(
        uuids.map((fileUuid) =>
          getRightworkAPI(API_get_learning_file, {
            company_id: homeCompany,
            learning_file_uuid: fileUuid,
          }).then((res) => ({
            fileUuid,
            lf: res?.data?.redux?.payload?.learning_file,
          })),
        ),
      );
      if (cancelled) return;
      for (const r of results) {
        if (r.status !== "fulfilled") continue; // transient — retry next tick
        const { fileUuid, lf } = r.value;
        if (lf?.video_status === "ready" && lf?.file) {
          setDeck((prev) => ({
            ...prev,
            cards: prev.cards.map((c) =>
              c.card_type === "video" && c.media_file_uuid === fileUuid && !c.media_url
                ? { ...c, media_url: lf.file }
                : c,
            ),
          }));
          delete seenAt[fileUuid];
        } else if (lf?.video_status === "failed") {
          // Explicit backend failure — safe to clear (the clip will never come).
          delete seenAt[fileUuid];
          clearDeadVideoCard(fileUuid);
        } else if (Date.now() - (seenAt[fileUuid] || Date.now()) > GIVE_UP_MS) {
          // Non-destructive backstop: stop polling a card stuck PENDING past any
          // real backend run (broker down), but DON'T clear it — selecting the
          // card re-fetches its status and the save gate flags it until ready.
          gaveUpVideoPollRef.current.add(fileUuid);
          delete seenAt[fileUuid];
        }
      }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // clearDeadVideoCard closes only over stable setState/refs; re-subscribing
    // on the pending-uuid set (pendingVideoKey) is the intended trigger.
  }, [pendingVideoKey, homeCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  // Streaming AI card generation. Mirrors QuizEditorModal.handleAiGenerate:
  // the AI modal awaits this fn and closes when it resolves — we resolve
  // immediately by NOT awaiting the streaming call, so the modal closes
  // and the user watches cards populate in the storyboard. Each card
  // arrives shaped like makeDefaultCard's output (plus a metadata
  // .media_suggestion that drives the placeholder treatment), so we
  // just attach a stable _clientId and append.
  // Centralizes the "stop the AI stream" call so save/close/unmount paths
  // can each invoke it without duplicating the abort + null-the-ref dance.
  const cancelInflightAi = () => {
    if (aiAbortRef.current) {
      aiAbortRef.current.abort();
      aiAbortRef.current = null;
    }
  };

  const handleAiGenerate = async ({
    sectionUuids = [],
    moduleUuids = [],
    fileUuids = [],
    pastedText = "",
    numCards,
    customContext = "",
    tone = "default",
    imageMode = "photos_ai",
  } = {}) => {
    // Abort any prior in-flight generation before starting a new one —
    // protects against re-firing the AI modal while a previous stream
    // is still running. Images from the prior run stop attaching too —
    // they'd race the new run's deck mutations.
    cancelInflightAi();
    cancelInflightAiImages();
    const controller = new AbortController();
    aiAbortRef.current = controller;

    // Remember this run's source documents so the card-image queue can
    // try a real-photo match against them before generating (parity
    // with the studio fill). Read via ref so queued jobs see the
    // current run's sources.
    aiSourceFileUuidsRef.current = fileUuids || [];
    // Generation policy for this run: "photos_ai" generates when no photo
    // matches; "photos" reuses only; "none" skips images entirely (gated at
    // the enqueue site below).
    aiAllowGenerationRef.current = imageMode === "photos_ai";
    // Fresh dedup set per run — last run's reused-photo hashes shouldn't
    // exclude photos from this one.
    usedImageSha1sRef.current = new Set();

    setAiGenStatus({ current: 0, total: numCards });
    let errorHandled = false;

    streamSwipeDeckGenerationAPI(
      API_generate_swipe_deck_with_ai,
      {
        company_id: homeCompany,
        learning_section_uuids: JSON.stringify(sectionUuids),
        learning_module_uuids: JSON.stringify(moduleUuids),
        file_upload_uuids: JSON.stringify(fileUuids),
        pasted_text: pastedText,
        num_cards: numCards,
        custom_context: customContext,
        tone: tone,
      },
      {
        onStarted: (total) => {
          if (aiAbortRef.current !== controller) return;
          setAiGenStatus({ current: 0, total: total ?? numCards });
        },
        onWarning: (text) => {
          if (aiAbortRef.current !== controller) return;
          // Context-scope notice from the backend (document still
          // indexing, retrieval fallback, truncation) — inform without
          // interrupting the stream.
          toast.warning(text, { autoClose: 6000 });
        },
        onCard: (card) => {
          // Drop events from a stream that's no longer the current run —
          // covers both the abort path (ref nulled) and the supersede
          // path (ref reassigned to a newer controller). Either way,
          // mutating deck state would corrupt the active session.
          if (aiAbortRef.current !== controller) return;
          const clientId =
            typeof window !== "undefined" && window.crypto?.randomUUID
              ? window.crypto.randomUUID()
              : `ai-${Date.now()}-${Math.random()}`;
          setDeck((prev) => {
            const nextCards = [
              ...(prev.cards || []),
              {
                ...card,
                _clientId: clientId,
                order_index: prev.cards.length,
              },
            ];
            return { ...prev, cards: nextCards };
          });
          setAiGenStatus((prev) =>
            prev ? { ...prev, current: prev.current + 1 } : null,
          );
          // Kick off this card's image while the rest of the deck is
          // still streaming — text cards illustrate progressively
          // instead of all-at-once after the stream closes.
          const suggestion = card?.metadata?.media_suggestion;
          if (
            imageMode !== "none" &&
            card?.card_type === "image_text" &&
            suggestion &&
            !card?.media_file_uuid
          ) {
            enqueueAiCardImage(clientId, suggestion, {
              headline: card?.content_json?.headline || "",
            });
          }
        },
        onDone: (count) => {
          if (aiAbortRef.current !== controller) return;
          setAiGenStatus(null);
          toast.success(
            `Generated ${count} ${count === 1 ? "card" : "cards"}.`,
            { autoClose: 3000 },
          );
        },
        onError: (msg) => {
          if (aiAbortRef.current !== controller) return;
          errorHandled = true;
          setAiGenStatus(null);
          toast.error(msg, { autoClose: 3000 });
        },
      },
      { signal: controller.signal },
    ).catch((err) => {
      // If a newer generation has taken over (or handleClose nulled
      // the ref), this older stream's settle path must NOT touch
      // aiGenStatus — doing so would hide progress for the run that's
      // actually active and make the new chip blink in/out.
      if (aiAbortRef.current !== controller) return;

      // Aborted by close/save/unmount — clear the chip silently.
      if (controller.signal.aborted || err?.name === "AbortError") {
        setAiGenStatus(null);
        return;
      }
      setAiGenStatus(null);
      if (!errorHandled) {
        console.error("AI swipe-deck generation failed", err);
        // Prefer the helper-propagated message (server's
        // system.message envelope on 4xx, or the SSE error event's
        // text). Falls back only when err has no useful message —
        // e.g. a fetch-level network failure with a stack-only Error.
        toast.error(
          err?.message || "Failed to generate cards. Please try again.",
          { autoClose: 3000 },
        );
      }
    });
    // Intentionally NOT awaited — return immediately so the AI modal
    // closes and the user sees streamed progress in the storyboard.
  };

  const handleUpdateCard = (idx, updatedCard) => {
    // Author manually overrode this card's image intent while its AI
    // image was still rendering — by changing the media itself (upload,
    // replace, remove) OR by switching the layout to text_only ("I
    // don't want an image here"). Either way: invalidate the pending
    // job so the late result can't clobber the manual choice, and stop
    // the tile shimmer immediately. Editor updates flow synchronously
    // from the current render, so `deck` is current here.
    const prevCard = deck.cards[idx];
    if (
      prevCard?._clientId &&
      generatingImageIds.has(prevCard._clientId) &&
      (prevCard.media_file_uuid !== updatedCard.media_file_uuid ||
        prevCard.media_url !== updatedCard.media_url ||
        (updatedCard.layout_variant === "text_only" &&
          prevCard.layout_variant !== "text_only"))
    ) {
      aiImageInvalidatedRef.current.add(prevCard._clientId);
      setGeneratingImageIds((prev) => {
        const next = new Set(prev);
        next.delete(prevCard._clientId);
        if (next.size === 0) setAiImageTotal(0);
        return next;
      });
    }
    setDeck((prev) => ({
      ...prev,
      cards: prev.cards.map((c, i) => (i === idx ? updatedCard : c)),
    }));
  };

  const handleRemoveCard = (idx) => {
    setDeck((prev) => ({
      ...prev,
      cards: prev.cards.filter((_, i) => i !== idx),
    }));
    if (selectedCardIdx === idx) setSelectedCardIdx(null);
    else if (selectedCardIdx !== null && selectedCardIdx > idx) {
      setSelectedCardIdx(selectedCardIdx - 1);
    }
  };

  const handleMoveCard = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setDeck((prev) => {
      const cards = [...prev.cards];
      const [moved] = cards.splice(fromIdx, 1);
      cards.splice(toIdx, 0, moved);
      return { ...prev, cards };
    });
    // Keep selectedCardIdx pointing at the same logical card after the
    // reorder. The naive "only update when the moved card was selected"
    // version silently swapped the editor pane to a different card
    // whenever a non-selected card was dragged across the selected
    // position — subsequent edits would then apply to the wrong card.
    // Functional setter avoids racing the deck update above.
    setSelectedCardIdx((s) => {
      if (s === null) return s;
      if (s === fromIdx) return toIdx; // selected card itself moved
      if (fromIdx < s && toIdx >= s) return s - 1; // crossed up past s
      if (fromIdx > s && toIdx <= s) return s + 1; // crossed down past s
      return s; // move stayed on one side of s
    });
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Save

  // closeAfter=false keeps the editor open (used by the Share popover's
  // "Save deck" so the public link reflects edits without losing the modal).
  const saveDeck = async (closeAfter) => {
    if (loadError) {
      // Detail never loaded — the editor holds the blank default (uuid null),
      // so a save would create a junk deck. Save is already disabled in the UI;
      // this is defense in depth.
      return;
    }
    if (!deck.name.trim()) {
      toast.error("Give your deck a name first.");
      return;
    }
    // Block save if any trivia card has no correct answer marked. The
    // editor surfaces an inline warning, but admins can still hit
    // Save while it's showing — without this gate, the deck publishes
    // and learners hit a card they can't possibly answer correctly
    // (the response endpoint rejects empty selected_indices, and
    // there's no non-empty selection that grades right). The BE has
    // the same validation as a defense-in-depth check; this gate just
    // surfaces the issue with a friendlier message before the round-trip.
    const invalidTriviaCardIdx = (deck.cards || []).findIndex((card) => {
      if (card.card_type !== "quiz") return false;
      const choices = card.content_json?.choices || [];
      return !choices.some((c) => c && c.is_correct);
    });
    if (invalidTriviaCardIdx >= 0) {
      toast.error(
        `Trivia on card ${invalidTriviaCardIdx + 1} needs at least one correct answer.`,
      );
      setSelectedCardIdx(invalidTriviaCardIdx);
      return;
    }
    // Block save if a video card is bound to a clip that hasn't finished
    // optimizing (media_file_uuid but no resolved media_url — typically a
    // just-split segment). The BE rejects this too, but with a generic message;
    // this points at the exact card. A failed clip self-clears (see the poll),
    // so this only fires for genuinely in-progress clips.
    const unreadyVideoCardIdx = (deck.cards || []).findIndex(
      (card) => card.card_type === "video" && card.media_file_uuid && !card.media_url,
    );
    if (unreadyVideoCardIdx >= 0) {
      toast.error(
        `The video on card ${unreadyVideoCardIdx + 1} is still optimizing. Give it a moment, then save.`,
      );
      setSelectedCardIdx(unreadyVideoCardIdx);
      return;
    }
    // Save commits the cards we already have; any cards still in flight
    // from the LLM would arrive after we POST and mutate hidden state.
    // Same for in-flight images: cards keep their media_suggestion in
    // metadata, so the author can generate post-save from the banner.
    cancelInflightAi();
    cancelInflightAiImages();
    setAiGenStatus(null);
    setIsSaving(true);
    try {
      const payload = buildDeckSavePayload(deck);
      payload.company_id = homeCompany;
      // Which tab owns the deck. Sent on every save so an Engagement deck never
      // lands in Training Content.
      payload.purpose = isEngagement ? "engagement" : "training";
      const res = await postRightworkAPI(API_post_learning_swipe_deck, payload);
      // Hand the saved deck (uuid + name) back so callers that embed it — e.g.
      // the section editor's "Create Swipe Deck" — can auto-insert the block.
      // Existing callers (the dashboard) just ignore the argument.
      const savedDeck =
        res?.data?.redux?.payload?.learning_swipe_deck || null;
      toast.success("Deck saved.");
      // Persist the "make directly assignable" setting on Save (only when it
      // changed) — like every other deck setting.
      if (
        savedDeck?.uuid &&
        !!directlyAssignable !== originalAssignableRef.current
      ) {
        await setDirectAssignability({
          companyId: homeCompany,
          assetType: "swipe_deck",
          assetUuid: savedDeck.uuid,
          isAssignable: !!directlyAssignable,
        });
        // Keep the baseline current so a second in-place save doesn't redo it.
        originalAssignableRef.current = !!directlyAssignable;
      }
      if (closeAfter) {
        // Closing save: notify the parent now, then close.
        if (onSaved) onSaved(savedDeck);
        pendingSavedDeckRef.current = null;
        // Mark the current deck content as saved so the unsaved-changes flag
        // clears, then close.
        deckBaselineRef.current = deckContentSnapshot(deck);
        // handleClose (not the bare onClose prop) so any redundant abort +
        // status clear stays consolidated in one path.
        handleClose();
      } else {
        // In-place save keeps the editor open. Notifying the parent now would
        // clear its embedding edit-context (→ duplicate block on a later save),
        // so QUEUE the saved deck and replay it on close (handleClose).
        pendingSavedDeckRef.current = savedDeck;
        // Rehydrate from the saved deck so newly-created cards adopt their server
        // UUIDs — otherwise the next save re-posts them uuid:null and the backend
        // delete-and-recreates them, losing card identity + poll/response
        // history. Baseline is taken from the rehydrated deck so dirty clears.
        const rehydrated = savedDeck ? normalizeBackendDeck(savedDeck) : deck;
        setDeck(rehydrated);
        deckBaselineRef.current = deckContentSnapshot(rehydrated);
      }
    } catch (err) {
      console.error("Save failed", err);
      const message =
        err?.response?.data?.error ||
        "Save failed. Check your card types and try again.";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => saveDeck(true);
  const handleSaveInPlace = () => saveDeck(false);

  // Wrap the parent's onClose so every close path (X button, MUI
  // backdrop click, esc key) aborts the AI stream first. Without this,
  // late SSE events would keep mutating local `deck` state on a hidden
  // modal until the next open() reset.
  const handleClose = React.useCallback(() => {
    // Flush any deferred in-place save to the parent first, so a rename/edit made
    // via the Share popover reaches the embedding section editor even when the
    // user closes with X. (A closing save already notified + cleared this.)
    if (pendingSavedDeckRef.current && onSaved) {
      onSaved(pendingSavedDeckRef.current);
    }
    pendingSavedDeckRef.current = null;
    cancelInflightAi();
    cancelInflightAiImages();
    setAiGenStatus(null);
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, onSaved]);

  // Safety net for unmount paths (route change, hot reload, etc.) that
  // don't go through handleClose. Refs only — no setState after unmount.
  React.useEffect(() => {
    return () => {
      if (aiAbortRef.current) {
        aiAbortRef.current.abort();
        aiAbortRef.current = null;
      }
      aiImageSessionRef.current += 1;
      aiImageQueueRef.current = [];
    };
  }, []);

  // Enable / disable / regenerate the public (no-login, untracked) share link.
  const handleSetPublicShare = async ({ is_enabled, regenerate }) => {
    if (!modalDataDeck?.uuid) return;
    setShareSaving(true);
    try {
      const res = await postRightworkAPI(API_post_set_swipe_deck_public_share, {
        company_id: homeCompany,
        learning_swipe_deck_uuid: modalDataDeck.uuid,
        is_enabled,
        regenerate: !!regenerate,
      });
      const share = res?.data?.redux?.payload?.public_share;
      if (share) setPublicShare(share);
      // The decks list shows this link's state (Shared badge, sharing filter,
      // open count), and a link can be toggled here without ever saving the
      // deck, so the list has to be told separately from onSaved.
      if (onPublicShareChange) onPublicShareChange();
      toast.success(
        is_enabled
          ? regenerate
            ? "New link generated. The old link no longer works."
            : "Public link is on."
          : "Public link turned off.",
        { autoClose: 3000 }
      );
    } catch (error) {
      const message = error?.response?.data?.system?.message;
      toast.error(message || "Couldn't update the share link.", { autoClose: 3000 });
    } finally {
      setShareSaving(false);
    }
  };

  const publicShareUrl =
    publicShare?.share_token
      ? `${window.location.origin}/s/${publicShare.share_token}`
      : "";

  // Unsaved-changes flag for the Share popover: live deck content vs. the last
  // loaded/saved baseline. Only relevant for existing decks (where Share works).
  // Work that would be lost by closing: edits to an existing deck, or any
  // content added to one that hasn't been created yet.
  const deckWouldLoseWork =
    !loadError &&
    ((deckBaselineRef.current != null &&
      deckContentSnapshot(deck) !== deckBaselineRef.current) ||
      !!directlyAssignable !== originalAssignableRef.current);

  const requestClose = () => {
    if (isSaving) return;
    if (deckWouldLoseWork) setConfirmDiscard(true);
    else handleClose();
  };

  const deckHasUnsavedChanges =
    Boolean(modalDataDeck?.uuid) &&
    deckBaselineRef.current != null &&
    deckContentSnapshot(deck) !== deckBaselineRef.current;

  const handleCopyShareUrl = () => {
    if (!publicShareUrl) return;
    try {
      navigator.clipboard.writeText(publicShareUrl);
      toast.success("Link copied.", { autoClose: 2000 });
    } catch (e) {
      toast.error("Couldn't copy. Select and copy the link manually.", { autoClose: 3000 });
    }
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Render

  const selectedCard =
    selectedCardIdx !== null ? deck.cards[selectedCardIdx] : null;

  // No `onClose` on the Modal: backdrop click + Escape must not dismiss an
  // editor with unsaved work; only the explicit Close (X) closes it. Mirrors
  // the Section Inputs modal convention.
  return (
    <Modal open={open} disableAutoFocus>
      <Box sx={modalStyle}>
        {/* Top bar */}
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 flex-shrink-0">
          <DeckCoverButton
            coverUrl={deck.cover_url}
            uploading={uploadingCover}
            onPick={handleCoverPick}
            onClear={handleCoverClear}
          />
          <div className="min-w-0 flex-1">
            <input
              type="text"
              value={deck.name}
              onChange={(e) => setDeck((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Untitled deck"
              className="block w-full text-lg font-semibold text-slate-900 bg-transparent border-none focus:outline-none focus:ring-0 px-1"
            />
            <input
              type="text"
              value={deck.description || ""}
              onChange={(e) =>
                setDeck((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Add a short description"
              className="mt-0.5 block w-full bg-transparent border-none px-1 text-sm text-slate-500 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            />
          </div>

          {!isEngagement && <ReuseSignalBanner count={sectionUsageCount} />}


          {(deck.cards || []).some((c) => c.card_type === "poll") && (
            <button
              type="button"
              onClick={() => setPollResultsOpen(true)}
              disabled={loadError}
              title="View poll results"
              className={[
                "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                "bg-slate-100 hover:bg-slate-200 text-slate-700",
                loadError ? "opacity-50 cursor-not-allowed" : "",
              ].join(" ")}
            >
              <PollOutlinedIcon style={{ fontSize: 18 }} />
              Poll results
            </button>
          )}

          {/* Public links live in the Engagement tab. A training deck only
              shows Share while it still has a link from before the split, so
              it stays manageable until the deck is moved; the server refuses
              a NEW link on a training deck. */}
          {(isEngagement || publicShare?.share_token) && (
          <button
            type="button"
            onClick={(e) => setShareAnchorEl(e.currentTarget)}
            disabled={loadError}
            title="Share a public link"
            className={[
              "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              publicShare?.is_enabled
                ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
                : "bg-slate-100 hover:bg-slate-200 text-slate-700",
              loadError ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <IosShareIcon style={{ fontSize: 18 }} />
            Share
          </button>
          )}

          {/* Header groups, left to right (matches the form editor): what the
              deck collects (Poll results / Share), a divider, then authoring
              (Preview), then the primary action. */}
          <div className="w-px h-6 bg-slate-200 mx-1 shrink-0" aria-hidden="true" />
          <button
            type="button"
            onClick={() => onPreview && onPreview(deck)}
            disabled={!deck.cards.length || loadError}
            className={[
              "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              deck.cards.length && !loadError
                ? "bg-slate-100 hover:bg-slate-200 text-slate-700"
                : "bg-slate-50 text-slate-300 cursor-not-allowed",
            ].join(" ")}
          >
            <VisibilityIcon style={{ fontSize: 18 }} />
            Preview
          </button>

          {aiGenStatus && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-600 text-white text-xs font-semibold shadow-sm">
              <AutoAwesomeIcon
                style={{ fontSize: 14 }}
                className="animate-pulse"
              />
              Generating {aiGenStatus.current}/{aiGenStatus.total}
            </span>
          )}

          {generatingImageIds.size > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold shadow-sm">
              <ImageIcon style={{ fontSize: 14 }} className="animate-pulse" />
              Images {Math.max(aiImageTotal - generatingImageIds.size, 0)}/{aiImageTotal}
            </span>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || loadError}
            className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white bg-violet-700 hover:bg-violet-800 transition-colors disabled:opacity-60"
          >
            {isSaving ? "Saving…" : "Save"}
          </button>

          <button
            onClick={requestClose}
            disabled={isSaving}
            aria-label="Close"
            title={isSaving ? "Finishing save…" : "Close"}
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left: card list — collapsible to give the storyboard more room.
              Collapses to a thin rail (the storyboard pane has no top toolbar
              to host a show button the way the Track Editor's center does). */}
          {showCardList ? (
            <CardListPane
              cards={deck.cards}
              selectedCardIdx={selectedCardIdx}
              onSelect={setSelectedCardIdx}
              onRemove={handleRemoveCard}
              onMove={handleMoveCard}
              onAddCard={(e) => setAddCardMenuAnchor(e.currentTarget)}
              onCollapse={() => setShowCardList(false)}
              footer={
                isEngagement ? null : (
                  <DirectAssignabilityToggle
                    value={directlyAssignable}
                    onChange={setDirectlyAssignable}
                  />
                )
              }
            />
          ) : (
            <div className="w-10 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col items-center pt-3">
              <IconButton
                size="small"
                onClick={() => setShowCardList(true)}
                title="Show cards"
                aria-label="Show cards"
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </div>
          )}

          {/* Center: storyboard. Clicks on the empty area of this pane
              (gaps between tiles, padding, etc.) deselect the active card —
              real card tiles carry `data-card-tile` so the closest() check
              skips them. The +Add tile is intentionally NOT marked, so
              clicking it deselects + opens the picker (the new card will
              auto-select on add). */}
          <div
            className="flex-1 overflow-y-auto bg-slate-50 px-8 py-6"
            onClick={(e) => {
              if (!e.target.closest("[data-card-tile]")) {
                setSelectedCardIdx(null);
              }
            }}
          >
            {isLoading ? (
              <div className="text-center text-sm text-slate-400 py-12">
                Loading…
              </div>
            ) : loadError ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <p className="text-slate-900 font-semibold">
                  Couldn't load this deck
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  It may have been deleted, or there was a network problem. Close
                  and try again.
                </p>
              </div>
            ) : deck.cards.length === 0 && !aiGenStatus ? (
              <EmptyStoryboard
                onAddCard={(e) => setAddCardMenuAnchor(e.currentTarget)}
                onGenerateAi={() => setShowAiModal(true)}
              />
            ) : (
              <StoryboardGrid
                cards={deck.cards}
                selectedCardIdx={selectedCardIdx}
                onSelect={setSelectedCardIdx}
                onAddCard={(e) => setAddCardMenuAnchor(e.currentTarget)}
                pendingCount={
                  aiGenStatus
                    ? Math.max(aiGenStatus.total - aiGenStatus.current, 0)
                    : 0
                }
                generatingImageIds={generatingImageIds}
              />
            )}
          </div>

          {/* Right: card editor — only renders when a card is selected, so
              the storyboard gets the full center pane otherwise. */}
          {selectedCard && (
            <div className="w-96 flex-shrink-0 border-l border-slate-200 bg-white overflow-y-auto">
              <RouteCardEditor
                card={selectedCard}
                onChange={(updated) => handleUpdateCard(selectedCardIdx, updated)}
                companyId={homeCompany}
                pollResults={
                  selectedCard.uuid ? pollResults[selectedCard.uuid] : null
                }
                onGenerateImage={() =>
                  handleGenerateCardImage(selectedCard._clientId)
                }
                isGeneratingImage={
                  Boolean(selectedCard._clientId) &&
                  generatingImageIds.has(selectedCard._clientId)
                }
              />
            </div>
          )}
        </div>

        {/* Card-type picker modal */}
        <SwipeCardTypePickerModal
          open={showCardTypePicker}
          onClose={() => setShowCardTypePicker(false)}
          onSelect={handleAddCard}
        />

        {/* Add-card menu — split between manual ("New card", which then
            asks for a card type) and AI-streamed ("Generate with AI"). */}
        <Menu
          anchorEl={addCardMenuAnchor}
          open={Boolean(addCardMenuAnchor)}
          onClose={() => setAddCardMenuAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MenuItem
            onClick={() => {
              setAddCardMenuAnchor(null);
              setShowCardTypePicker(true);
            }}
          >
            <ListItemIcon>
              <StyleOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="New card" secondary="Pick a card type" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAddCardMenuAnchor(null);
              setShowVideoSplitModal(true);
            }}
          >
            <ListItemIcon>
              <ContentCutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Split a video into cards"
              secondary="Trim one long video into several clips"
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAddCardMenuAnchor(null);
              setShowAiModal(true);
            }}
          >
            <ListItemIcon>
              <AutoAwesomeIcon fontSize="small" sx={{ color: "#6d28d9" }} />
            </ListItemIcon>
            <ListItemText
              primary="Generate with AI"
              secondary="Stream cards from your training content"
            />
          </MenuItem>
        </Menu>

        {/* AI generator modal — closes immediately on submit; the deck
            populates as cards stream in via handleAiGenerate. */}
        <AiSwipeDeckGeneratorModal
          open={showAiModal}
          handleClose={() => setShowAiModal(false)}
          homeCompany={homeCompany}
          sectionsData={sectionsData}
          modulesData={modulesData}
          onGenerate={handleAiGenerate}
        />

        {/* Video splitter — trims one long upload into several video cards,
            appended via handleCreateCardsFromSplit (each polls to READY). */}
        <SwipeVideoSplitModal
          open={showVideoSplitModal}
          onClose={() => setShowVideoSplitModal(false)}
          companyId={homeCompany}
          onCreateCards={handleCreateCardsFromSplit}
        />

        {/* Public share popover */}
        <Popover
          open={Boolean(shareAnchorEl)}
          anchorEl={shareAnchorEl}
          onClose={() => setShareAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { borderRadius: "12px", mt: 1 } } }}
        >
          <div className="w-80 p-4">
            <div className="text-sm font-semibold text-slate-900">
              Public share link
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {isEngagement
                ? "Anyone with the link can open this deck, no login. Opens and poll votes are counted anonymously."
                : "Anyone with the link can open this deck, no login. Opens and poll results are counted anonymously, but completion is not tracked. Not for confidential or graded-of-record content."}
            </p>

            {deckHasUnsavedChanges && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                <div className="text-xs text-amber-800">
                  You have unsaved changes. Save the deck for them to appear in
                  the public link.
                </div>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={handleSaveInPlace}
                  className="mt-1.5 text-xs font-semibold text-amber-900 underline disabled:opacity-60"
                >
                  {isSaving ? "Saving…" : "Save deck"}
                </button>
              </div>
            )}

            {!modalDataDeck?.uuid ? (
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500">
                Save the deck first to create a share link.
              </div>
            ) : !publicShare?.is_enabled ? (
              <button
                type="button"
                disabled={shareSaving}
                onClick={() => handleSetPublicShare({ is_enabled: true })}
                className="mt-3 w-full rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium py-2 disabled:opacity-60"
              >
                {shareSaving ? "Creating…" : "Create public link"}
              </button>
            ) : (
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={publicShareUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700"
                  />
                  <button
                    type="button"
                    onClick={handleCopyShareUrl}
                    className="shrink-0 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-xs font-medium px-3 py-1.5"
                  >
                    Copy
                  </button>
                  <QrCodePopover
                    url={publicShareUrl}
                    label={deck.name || "deck"}
                    className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white hover:border-violet-400 text-slate-700 text-xs font-medium px-2.5 py-1.5"
                  />
                </div>
                <div className="text-xs text-slate-500">
                  {publicShare.open_count}{" "}
                  {publicShare.open_count === 1 ? "open" : "opens"}
                </div>
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    disabled={shareSaving}
                    onClick={() =>
                      handleSetPublicShare({ is_enabled: true, regenerate: true })
                    }
                    className="text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-60"
                  >
                    Regenerate link
                  </button>
                  <button
                    type="button"
                    disabled={shareSaving}
                    onClick={() => handleSetPublicShare({ is_enabled: false })}
                    className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-60"
                  >
                    Turn off
                  </button>
                </div>
              </div>
            )}
          </div>
        </Popover>

        <SwipeDeckPollResultsModal
          open={pollResultsOpen}
          onClose={() => setPollResultsOpen(false)}
          deckName={deck.name}
          cards={deck.cards}
          pollResults={pollResults}
          hideAssigned={isEngagement}
        />

        {confirmDiscard && (
          <DiscardChangesConfirm
            noun="deck"
            isNew={!modalDataDeck?.uuid}
            onKeepEditing={() => setConfirmDiscard(false)}
            onDiscard={() => {
              setConfirmDiscard(false);
              handleClose();
            }}
          />
        )}
      </Box>
    </Modal>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Card editor router

function RouteCardEditor({ card, onChange, companyId, pollResults, onGenerateImage, isGeneratingImage }) {
  switch (card.card_type) {
    case "image_text":
      return (
        <SwipeCardImageTextEditor
          card={card}
          onChange={onChange}
          companyId={companyId}
          onGenerateImage={onGenerateImage}
          isGeneratingImage={isGeneratingImage}
        />
      );
    case "video":
      return (
        // Key by the card's stable id so navigating to a different video card
        // REMOUNTS the editor — its local videoStatus/poll must not leak across
        // cards (a pending card A would otherwise inherit a resolved card B's
        // status and stop polling). _clientId is stable across this card's own
        // edits (including its upload), so an in-card upload doesn't remount.
        <SwipeCardVideoEditor
          key={card._clientId}
          card={card}
          onChange={onChange}
          companyId={companyId}
        />
      );
    case "poll":
      return (
        <SwipeCardPollEditor
          card={card}
          onChange={onChange}
          companyId={companyId}
          results={pollResults}
        />
      );
    case "quiz":
      return (
        <SwipeCardTriviaEditor
          card={card}
          onChange={onChange}
          companyId={companyId}
        />
      );
    default:
      return (
        <div className="p-6 text-sm text-slate-500">
          Unknown card type: {card.card_type}
        </div>
      );
  }
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Card list pane (left)

function CardListPane({
  cards,
  selectedCardIdx,
  onSelect,
  onRemove,
  onMove,
  onAddCard,
  onCollapse,
  footer,
}) {
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    onMove(result.source.index, result.destination.index);
  };

  return (
    <div className="w-56 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col">
      <div className="px-3 py-3 border-b border-slate-200 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Cards ({cards.length})
        </span>
        <IconButton size="small" onClick={onCollapse} title="Hide cards" aria-label="Hide cards">
          <MenuIcon fontSize="small" />
        </IconButton>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="swipe-deck-cards">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {cards.map((card, idx) => {
                  const meta = CARD_TYPE_META[card.card_type] || {};
                  const Icon = meta.Icon || ImageIcon;
                  const title =
                    card.content_json?.headline ||
                    card.content_json?.title ||
                    card.content_json?.question ||
                    meta.label ||
                    "Untitled";
                  const isSelected = idx === selectedCardIdx;
                  const draggableId =
                    card._clientId || card.uuid || `card-${idx}`;
                  return (
                    <Draggable
                      key={draggableId}
                      draggableId={draggableId}
                      index={idx}
                    >
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          onClick={() => onSelect(idx)}
                          className={[
                            "group rounded-lg px-2 py-2 mb-1 cursor-pointer transition-colors flex items-center gap-2",
                            snapshot.isDragging
                              ? "bg-violet-50 shadow-md border-l-2 border-violet-700"
                              : isSelected
                              ? "bg-violet-50 border-l-2 border-violet-700"
                              : "hover:bg-slate-50 border-l-2 border-transparent",
                          ].join(" ")}
                        >
                          <div
                            {...dragProvided.dragHandleProps}
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-300 hover:text-slate-500 cursor-grab"
                          >
                            <DragIndicatorIcon style={{ fontSize: 16 }} />
                          </div>
                          <div
                            className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
                            style={{
                              backgroundColor:
                                TYPE_BADGE_BG[card.card_type] || "#e2e8f0",
                              color:
                                TYPE_BADGE_FG[card.card_type] || "#475569",
                            }}
                          >
                            <Icon style={{ fontSize: 16 }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-slate-400 leading-tight">
                              Card {idx + 1}
                            </div>
                            <div className="text-sm text-slate-900 truncate">
                              {title || "Untitled"}
                            </div>
                          </div>
                          <Tooltip title="Delete card">
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemove(idx);
                                }}
                                aria-label="Delete card"
                              >
                                <CloseIcon style={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {footer && (
        <div className="border-t border-slate-200 px-3 py-2.5 shrink-0">
          {footer}
        </div>
      )}
      <div className="h-14 border-t border-slate-200 px-3 bg-white flex items-center shrink-0">
        <button
          type="button"
          onClick={onAddCard}
          className="w-full py-2 px-3 bg-violet-50 hover:bg-violet-100 text-violet-700 text-sm rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
        >
          <AddIcon fontSize="small" />
          Add Card
        </button>
      </div>
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Storyboard grid (center)

function StoryboardGrid({
  cards,
  selectedCardIdx,
  onSelect,
  onAddCard,
  pendingCount = 0,
  generatingImageIds,
}) {
  // Always 4 columns, capped at max-w-6xl and centered (a bit wider than the
  // old 5xl, so tiles are slightly larger) and never reflowing to a 5th.
  return (
    <div className="grid grid-cols-4 gap-4 max-w-6xl mx-auto">
      {cards.map((card, idx) => (
        <StoryboardTile
          key={idx}
          card={card}
          index={idx}
          isSelected={idx === selectedCardIdx}
          onSelect={() => onSelect(idx)}
          isGeneratingImage={Boolean(
            card._clientId && generatingImageIds?.has(card._clientId),
          )}
        />
      ))}
      {pendingCount > 0 &&
        Array.from({ length: pendingCount }).map((_, i) => (
          <PendingCardTile key={`pending-${i}`} index={cards.length + i} />
        ))}
      <AddCardPlaceholderTile onClick={onAddCard} />
    </div>
  );
}

// Storyboard tile placeholder shown for each AI card that hasn't streamed
// in yet. Same 9:16 footprint as a real tile so the grid doesn't reflow
// when a real card replaces a pending one — feels like cards are
// materializing into existing slots.
function PendingCardTile({ index }) {
  return (
    <div
      className="relative aspect-[9/16] rounded-2xl overflow-hidden ring-1 ring-violet-200 bg-gradient-to-br from-violet-50 to-violet-100 flex items-center justify-center"
      aria-hidden="true"
    >
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-semibold flex items-center gap-1 z-10 bg-violet-600 text-white">
        <AutoAwesomeIcon style={{ fontSize: 12 }} className="animate-pulse" />
        AI
      </div>
      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/30 text-white text-[10px] font-semibold z-10">
        {index + 1}
      </div>
      <div className="flex flex-col items-center gap-3 px-3">
        <AutoAwesomeIcon
          className="text-violet-500 animate-pulse"
          style={{ fontSize: 32 }}
        />
        <div className="w-full space-y-1.5">
          <div className="h-2 rounded bg-violet-200 animate-pulse w-[80%] mx-auto" />
          <div className="h-2 rounded bg-violet-200 animate-pulse w-[55%] mx-auto" />
        </div>
      </div>
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Deck cover thumbnail / upload (top bar)

// 40×40 thumbnail in the modal header. Empty state is a dashed-border
// upload tile; populated state shows the image with a hover-revealed X
// to clear. Clicking the populated thumbnail picks a fresh file and
// replaces — admins don't have to clear first.
function DeckCoverButton({ coverUrl, uploading, onPick, onClear }) {
  if (uploading) {
    return (
      <div className="w-10 h-10 rounded-lg flex-shrink-0 bg-slate-100 flex items-center justify-center text-xs text-slate-500">
        …
      </div>
    );
  }
  if (coverUrl) {
    return (
      <div className="relative w-10 h-10 flex-shrink-0 group">
        <label className="block w-full h-full rounded-lg overflow-hidden cursor-pointer border border-slate-200">
          <img
            src={coverUrl}
            alt="Deck cover"
            className="w-full h-full object-cover"
            draggable={false}
          />
          <input
            type="file"
            accept="image/*"
            onChange={onPick}
            className="hidden"
          />
        </label>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          aria-label="Remove cover"
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-700 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          <CloseIcon style={{ fontSize: 11 }} />
        </button>
      </div>
    );
  }
  return (
    <Tooltip title="Upload deck cover">
      <label className="w-10 h-10 rounded-lg flex-shrink-0 border-2 border-dashed border-slate-300 hover:border-violet-500 hover:bg-violet-50 cursor-pointer flex items-center justify-center text-slate-400 hover:text-violet-600 transition-colors">
        <AddPhotoAlternateOutlinedIcon style={{ fontSize: 20 }} />
        <input
          type="file"
          accept="image/*"
          onChange={onPick}
          className="hidden"
        />
      </label>
    </Tooltip>
  );
}

// Trailing tile in the storyboard. Same dimensions as a real card so the
// grid stays even, but rendered as a dashed-outline placeholder with a
// big "+" — matches the Opus storyboard pattern. Clicking it opens the
// card type picker, same flow as the left-list "Add Card" button.
function AddCardPlaceholderTile({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add card"
      className="aspect-[9/16] rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 hover:border-violet-500 hover:bg-violet-50 transition-colors flex items-center justify-center text-slate-400 hover:text-violet-600"
    >
      <AddIcon style={{ fontSize: 36 }} />
    </button>
  );
}

// ScaledCardPreview (the WYSIWYG scaled player-card renderer) + its helpers
// now live in ./ScaledCardPreview so the AI studio's embedded-deck strip can
// share the exact same rendering. Imported at the top of this file.

function StoryboardTile({ card, index, isSelected, onSelect, isGeneratingImage }) {
  const meta = CARD_TYPE_META[card.card_type] || {};
  const Icon = meta.Icon;
  // AI-generated cards leave media_file_uuid empty but stash a text hint on
  // metadata.media_suggestion (ONLY the AI sets it — manually-created cards
  // start with empty metadata). We do NOT overlay that suggestion on the tile:
  // it's already shown in the right-hand IMAGE panel, and covering the tile hid
  // the card's headline/body. The tile just renders the normal card via the
  // scaled preview (image placeholder for split, big text for full-bleed).
  //
  // The only full-card overlay left is the transient "Generating image…" state,
  // and it only applies to image-first image_text cards — quiz/poll/video and
  // text_only render their own content and must not be covered.
  const mediaUrl = card.media_url || null;
  const mediaSuggestion = card.metadata?.media_suggestion || null;
  const layout = card.layout_variant || "full_bleed";
  const isImageFirstCard =
    card.card_type === "image_text" && layout !== "text_only";

  // Tile is a div+role="button", not a real <button>, because the scaled
  // player preview inside it contains nested <button>s (mute toggle,
  // poll choices, trivia answers). Nested interactive elements are
  // invalid DOM and trigger React nesting warnings; the inert+aria-hidden
  // applied to the inner preview keeps those nested controls out of the
  // tab order and a11y tree, but we still can't put a <button> around
  // them. role+tabIndex+keyboard handler preserves the same affordance.
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      aria-pressed={isSelected}
      data-card-tile
      className={[
        "relative aspect-[9/16] rounded-2xl overflow-hidden bg-black text-left transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-violet-400",
        isSelected
          ? "ring-4 ring-violet-600 shadow-xl scale-[1.02]"
          : "ring-1 ring-slate-200 hover:ring-2 hover:ring-slate-300 hover:shadow-lg",
      ].join(" ")}
    >
      <ScaledCardPreview card={card} />

      {/* While a generated image renders, show a transient working state. The
          suggested-image text is NOT overlaid on the card — it's already shown
          in the right-hand IMAGE panel, and covering the tile hid the card's
          headline/body. The tile just renders the normal card (ScaledCardPreview:
          image placeholder for split, big text for full-bleed). */}
      {isGeneratingImage && !mediaUrl && isImageFirstCard && (
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-violet-100 to-violet-200 flex flex-col items-center justify-center gap-2">
          <AutoAwesomeIcon
            className="text-violet-600 animate-pulse"
            style={{ fontSize: 26 }}
          />
          <p className="text-[10px] text-violet-800 font-semibold uppercase tracking-wide">
            Generating image…
          </p>
          {mediaSuggestion && (
            <p className="text-[10px] text-violet-900 italic leading-snug line-clamp-2 px-3 text-center">
              {mediaSuggestion}
            </p>
          )}
        </div>
      )}

      {/* Type badge */}
      <div
        className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-semibold flex items-center gap-1 z-10"
        style={{
          backgroundColor: TYPE_BADGE_BG[card.card_type],
          color: TYPE_BADGE_FG[card.card_type],
        }}
      >
        {Icon && <Icon style={{ fontSize: 12 }} />}
        {meta.label}
      </div>

      {/* Position number */}
      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/40 text-white text-[10px] font-semibold z-10">
        {index + 1}
      </div>
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Empty storyboard

function EmptyStoryboard({ onAddCard, onGenerateAi }) {
  return (
    // Same vertical centering as the form/quiz empty panes (h-full inside a
    // flex-1 scroll container), so the three editors' empty states sit at the
    // same eye line.
    <div className="h-full flex items-center justify-center">
      <div className="max-w-md text-center px-6">
        <div className="text-5xl mb-4">📚</div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Start with your first card
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          Decks are short: typically 5 to 10 cards, around 90 seconds total.
          Each card is one focused idea.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onAddCard}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white px-4 py-2 font-medium transition-colors"
          >
            <AddIcon style={{ fontSize: 20 }} />
            Add a card
          </button>
          {onGenerateAi && (
            <button
              type="button"
              onClick={onGenerateAi}
              className="inline-flex items-center gap-2 rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50 px-4 py-2 font-medium transition-colors"
            >
              <AutoAwesomeIcon style={{ fontSize: 18 }} />
              Generate with AI
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SwipeDeckInputsModal;
